import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { defaultDateStrings } from "../Common/Constants";
import { getParamsSelector } from "../Common/Slices/ParamSlice";
import {RunningPeriodsApi} from "./api"
import RunningPeriodsChart from "../Common/Components/Charts/RunningPeriodsChart"
import ComboChart from "../Common/Components/Charts/ComboChart";
import ErrorMessage from "../Common/Components/ErrorMessage";
import Spinner from "../Common/Components/spinner";
import { getDevicesSelector } from "../Devices/Slice";
import Format from "../Common/Util_methods/formatParamOptions";
import { RunningPeriods } from "../Common/Types/types";
import {isEqual,sortBy, difference} from "lodash"
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {ListView} from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons"
import { getRunningPeriodsOptionsSelector, updateSliceBucketCount, updateSliceBucketSize } from "./runningPeriodsOptionSlice";
import { useAppDispatch } from "../Core/store.hooks";
import {resetSelectedBucket} from "./runningPeriodsOptionSlice"

interface RunningPeriodsWidgetProps{
    id: string;
    header: string;
}

interface Categories{
    [key:string]:string | number
}

const RunningPeriodsWidget = (props:RunningPeriodsWidgetProps) =>{
    const [chartData, updateChartData] = useState([]);
    const [visibleDialog, setVisibleDialog] = React.useState<boolean>(false);
    const [dataLoading, updateDataLoading] = useState<boolean>(false);
    const [devicesWithNoData, updateDevicesWithNoData] = useState([]);
    const [dataBuckets,updateDataBuckets] = useState([]);
    const params = useSelector(getParamsSelector);
    const options = useSelector(getRunningPeriodsOptionsSelector);
    const deviceObjects = useSelector(getDevicesSelector);

    const dispatch = useAppDispatch();

    useEffect(()=>{
        if(params.devicesWithSerial.length >0){
            if(dataLoading === false){
                updateDataLoading(true);
                fetchRunningPeriods();
            }
        }else{
            updateChartData([])
            updateDataLoading(false);
        }
        dispatch(resetSelectedBucket())
    },[params])
    useEffect(()=>{
        updateChartData(formatRunningPeriods(dataBuckets[options.selectedBucket]))
    },[options.selectedBucket])

    const fetchRunningPeriods = () =>{
        RunningPeriodsApi.getRunningPeriods(params).then((res:any)=>{
            dispatch(updateSliceBucketCount(res.length))
            updateDataBuckets(res);
            updateDataLoading(false);
            updateChartData(formatRunningPeriods(res[options.selectedBucket]))
            checkResult(res)
        }).catch(error =>{
            updateChartData(null);
            updateDataLoading(false);
        });
    }

    const checkResult = (res:RunningPeriods[]) =>{
        let deviceBuckets:string[] = [];
        res.forEach((bucket:any)=>{
            const resultDevices = bucket.map((obj: { deviceWithSerial: string; })=>obj.deviceWithSerial);
            deviceBuckets.push(resultDevices)
        })
        //
        let foundDevices:string[] = [];
        params.devicesWithSerial.forEach((singleDevice:string)=>{
            deviceBuckets.forEach((singleBucket)=>{
                if(singleBucket.includes(singleDevice)){
                    if(!foundDevices.includes(singleDevice)){
                        foundDevices.push(singleDevice);
                    }
                }
            })
        })
        //
        if(!isEqual(sortBy(foundDevices),sortBy(params.devicesWithSerial))){
            const devicesWithNoData = difference(sortBy(params.devicesWithSerial),sortBy(foundDevices))
            let arr:string[] = []
            devicesWithNoData.forEach((item)=>{
                item = Format.CustomerByDeviceWithSerial(deviceObjects,item)+" "+item
                arr.push(item)
            }) 
            updateDevicesWithNoData(arr)
            setVisibleDialog(true);
        }
    }

    const formatRunningPeriods = (res:RunningPeriods[]) =>{
        if(res != null){
            let obj1:Categories = {"category":"Very Short"};
            let obj2:Categories = {"category":"Short"};
            let obj3:Categories = {"category":"Medium Short"};
            let obj4:Categories = {"category":"Medium Long"};
            let obj5:Categories = {"category":"Long"};
            let obj6:Categories = {"category":"Very Long"};
            for(let i = 0;i<res.length;i++){
                let customerDevice = Format.CustomerByCadID(deviceObjects,res[i].customerAssemblyDeviceID)+" "+res[i].deviceWithSerial;

                if(res[i].deviceWithSerial === null){
                    customerDevice = "no data for selected devices"
                }
                obj1[customerDevice] = res[i].veryShort
                obj2[customerDevice] = res[i].short
                obj3[customerDevice] = res[i].mediumShort
                obj4[customerDevice] = res[i].mediumLong
                obj5[customerDevice] = res[i].long
                obj6[customerDevice] = res[i].veryLong
            }
            return ([obj1,obj2,obj3,obj4,obj5,obj6])
        }else{
            return [];
        }
    }

    const toggleDialog = () => {
        setVisibleDialog(!visibleDialog);
      };

    const listViewItem = (props:any) => {
        let item = props.dataItem;
        return (
            <div className="row p-2 border-bottom align-middle" style={{ margin: 0 }}>
                {item}
            </div>
        )
    }

    function getBucketSpan(){
        if(dataBuckets[options.selectedBucket].length > 0){
            return dataBuckets[options.selectedBucket][0].bucketSpan
        }
        return "no data";
    }   
    
    if(params.devicesWithSerial.length < 1){
        return <div>Select customer device to show data</div>
    }
    if (dataLoading) return <Spinner text="data" />;
    if (chartData == null) {
        //proper error handling should be added with promise error response
        return (
            <ErrorMessage
                text={"Oops something went wrong, unable to get data."}
                id={props.id}
                header={props.header}
            />
        );
    }
    if (chartData != null && chartData.length === 0) {
        return (
            <ErrorMessage
                text={"No data found for selected filters."}
                id={props.id}
                header={props.header}
            />
        );
    }
    return (
        <Fragment>
            <RunningPeriodsChart id="9" data={chartData} timeSpan={getBucketSpan()}/>
            {visibleDialog && (
                <Dialog title={"Listed devices had no data:"} onClose={toggleDialog}>
                {/* <p style={{ margin: "25px", textAlign: "center" }}>
                    Listed devices had no data:
                </p> */}
                <ListView
                    data={devicesWithNoData}
                    item={listViewItem}
                    style={{ width: "100%" }}
                />
                    <DialogActionsBar>
                        <button
                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                        onClick={toggleDialog}
                        >
                        OK
                        </button>
                    </DialogActionsBar>
                </Dialog>)}
        </Fragment>
    )
}

export default RunningPeriodsWidget;